import React, { useRef, useState, useCallback } from "react";
import {
  Grid,
  Box,
  IconButton,
  Button,
  Typography,
  Card,
  TextField,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CloseIcon from "@mui/icons-material/Close";
import Webcam from "react-webcam";
import {
  useUploadDriverLicenseBase64Mutation,
  useConfirmDriverLicenseMutation,
  useUploadDriverLicenseMutation,
  useCreateDriverMutation,
} from "../../../Api/attoDeskApi";
import { useStyles } from "../../../theme/appStyles";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { FieldArray, FormikProvider, useFormik } from "formik";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { useNotifier } from "../../../Core/Notifier";
import { GenderalidationDriver } from "../../../Core/Enum/enum";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";

const videoConstraints = { facingMode: "environment" };

type FormValues = {
  // id: string;
  license_number: string;
  first_name: string;
  last_name: string;
  address: string;
  sex: string;
  date_of_birth: Dayjs | null;
  issue_date: Dayjs | null;
  expiry_date: Dayjs | null;
  license_img: string;
  raw_text: string;
};

function normalizeGender(value?: string | null): string {
  if (!value) return "";

  const lower = value.toLowerCase();

  if (lower.startsWith("m")) return GenderalidationDriver.Male;
  if (lower.startsWith("f")) return GenderalidationDriver.Female;

  return ""; // fallback if unknown
}

export default function IDImageCapture() {
  const classes = useStyles();
  const webcamRef = useRef<Webcam>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [rawText, setRawText] = useState("");
  const [parsed, setParsed] = useState<any>(null);
  const [message, setMessage] = useState("");
  const { showErrorMessage, showMessage } = useNotifier();
  const [uploading, setUpLoading] = useState(false);
  const [getlicense_img, setgetlicense_img] = useState<string>("");
  const navigate = useNavigate();

  const [uploadFile] = useUploadDriverLicenseMutation();
  const [uploadBase64] = useUploadDriverLicenseBase64Mutation();
  const [confirmLicense] = useConfirmDriverLicenseMutation();
  const [newCompany, { isLoading }] = useCreateDriverMutation();

  const captureFromWebcam = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        setPreviewUrl(imageSrc);
        const byteString = atob(imageSrc.split(",")[1]);
        const mimeString = imageSrc.split(",")[0].split(":")[1].split(";")[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++)
          ia[i] = byteString.charCodeAt(i);
        const blob = new Blob([ab], { type: mimeString });
        const file = new File([blob], "captured.jpg", { type: mimeString });
        setSelectedImage(file);
        setCameraOpen(false);
      }
    }
  }, []);

  const handleUploadToOCR = async () => {
    setMessage("");
    setUpLoading(true);
    if (!selectedImage && !previewUrl) {
      setMessage("Please select or capture an image first.");
      return;
    }

    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      let response: Response;

      if (selectedImage) {
        const formData = new FormData();
        formData.append("id_image", selectedImage);

        response = await fetch(`${baseUrl}/ocr/upload`, {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
          },
        });
      } else if (previewUrl) {
        response = await fetch(`${baseUrl}/ocr/upload-base64`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ image_base64: previewUrl }),
        });
      } else {
        // This is just a safety check, should never happen due to the first if
        throw new Error("No image selected for upload");
      }

      const data = await response.json();

      if (data.success) {
        setRawText(data.raw || "");
        setParsed(data.parsed || {});
        setgetlicense_img(data.image_url || "");
        setMessage("Text extracted successfully.");
      } else {
        setMessage(data.message || "Failed to extract text.");
      }
    } catch (err: any) {
      console.error("OCR Upload Error:", err);
      setMessage(err?.data?.message || "Error uploading image.");
    } finally {
      setUpLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!parsed) return;
    try {
      const res = await confirmLicense({
        parsed: { ...parsed, raw: rawText },
      }).unwrap();
      if (res.success) setMessage(`✅ License saved with ID: ${res.id}`);
    } catch (err: any) {
      setMessage(err?.data?.message || "Error saving license.");
    }
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      license_number: parsed?.licenseNumber || "",
      first_name: toProperCase(parsed?.firstName) || "",
      last_name: toProperCase(parsed?.lastName) || "",
      address: toProperCase(parsed?.address) || "",
      sex: normalizeGender(parsed?.sex),
      date_of_birth: parsed?.dateOfBirth
        ? dayjs(parsed.dateOfBirth.toString())
        : null,
      issue_date: parsed?.issueDate ? dayjs(parsed.issueDate.toString()) : null,
      expiry_date: parsed?.expiryDate
        ? dayjs(parsed.expiryDate.toString())
        : null,
      license_img: getlicense_img || "",
      raw_text: message || "",
    },
    enableReinitialize: true,

    onSubmit: async (values, { resetForm }) => {
      try {
        const updatedData = {
          // id: Number(values.id),
          license_number: values.license_number,
          first_name: toProperCase(values.first_name),
          last_name: toProperCase(values.last_name),
          address: toProperCase(values.address),
          sex: values.sex,
          date_of_birth: values.date_of_birth
            ? values.date_of_birth.format("YYYY-MM-DD")
            : null,
          issue_date: values.issue_date
            ? values.issue_date.format("YYYY-MM-DD")
            : null,
          expiry_date: values.expiry_date
            ? values.expiry_date.format("YYYY-MM-DD")
            : null,
          license_img: values.license_img,
          raw_text: values.raw_text,
        };

        const response = await newCompany(updatedData).unwrap();
        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          navigate(`/driver-history/${response.data.id}`);
          resetForm();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });
  const handleAddProductClick = () => {
    navigate("/driver/licence/manual/create");
  };

  return (
    <Grid container spacing={2}>
      <Grid item lg={12} md={12} sm={12} xs={12}>
        <Card className={classes.card} sx={{ minHeight: "400px" }}>
          <Grid
            container
            justifyContent={"space-between"}
            spacing={5}
            sx={{ borderBottom: 2, padding: 2 }}
            className={classes.titleGrid}
          >
            <Grid item alignContent={"center"}>
              <Typography className={classes.cardTitle}>
                Add Driver License Image
              </Typography>
            </Grid>
            <Grid
              item
              sx={{
                display: "flex",
                justifyContent: {
                  xs: "flex-start",
                  md: "flex-end",
                },
              }}
            >
              <Grid
                container
                spacing={1}
                justifyContent="flex-end"
                sx={{
                  display: "flex",
                  justifyContent: {
                    xs: "flex-end",
                  },
                }}
              >
                <Grid item>
                  <Link
                    to={`/driver/licence/manual/create`}
                    className={classes.iconLinkPropNormal}
                  >
                    <Button
                      // variant="contained"
                      className={classes.addNewButton}
                      onClick={handleAddProductClick}
                    >
                      Manualy Add New
                    </Button>
                  </Link>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid container spacing={2} p={"24px"}>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              {previewUrl && (
                <Box className={classes.imgBoxDriver}>
                  {previewUrl && (
                    <img
                      src={previewUrl}
                      alt="Selected"
                      style={{
                        maxWidth: "100%",
                        maxHeight: 300,
                        borderRadius: 8,
                        marginBottom: "16px",
                      }}
                    />
                  )}
                </Box>
              )}
              <Box sx={{ textAlign: "center" }}>
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="icon-button-file-1"
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setSelectedImage(file);
                      setPreviewUrl(URL.createObjectURL(file));
                    }
                  }}
                />
                <label htmlFor="icon-button-file-1">
                  <IconButton color="primary" component="span">
                    <CloudUploadIcon sx={{ fontSize: 45 }} />
                  </IconButton>
                </label>

                <IconButton color="primary" onClick={() => setCameraOpen(true)}>
                  <CameraAltIcon sx={{ fontSize: 45 }} />
                </IconButton>

                {cameraOpen && (
                  <Box
                    mt={2}
                    p={2}
                    border="2px solid #ccc"
                    borderRadius={2}
                    sx={{ background: "#f9f9f9" }}
                  >
                    <Webcam
                      audio={false}
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                      videoConstraints={videoConstraints}
                      style={{ width: "100%", borderRadius: 8 }}
                    />
                    <Box mt={2} display="flex" gap={1}>
                      <Button variant="contained" onClick={captureFromWebcam}>
                        Capture
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        onClick={() => setCameraOpen(false)}
                      >
                        Close
                      </Button>
                    </Box>
                  </Box>
                )}

                <Box mt={2}>
                  <Button
                    variant="contained"
                    onClick={handleUploadToOCR}
                    disabled={!selectedImage && !previewUrl}
                  >
                    {uploading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Upload to Driver License"
                    )}
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Card>
      </Grid>
      {uploading ? (
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Card className={classes.card} sx={{ minHeight: "400px" }}>
            <Grid container spacing={2} sx={{ padding: 2 }}>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="10vh"
                >
                  <CircularProgress />
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      ) : (
        <>
          {(parsed || message || rawText) && (
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid container spacing={2} sx={{ padding: 2 }}>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    {message && (
                      <Alert
                        severity={
                          message === "Text extracted successfully."
                            ? "success"
                            : "error"
                        }
                        // icon={
                        //   message === "Text extracted successfully." ? (
                        //     <CheckCircleIcon fontSize="inherit" />
                        //   ) : (
                        //     <ErrorIcon fontSize="inherit" />
                        //   )
                        // }
                        sx={{ mt: 2 }}
                      >
                        {message}
                      </Alert>
                    )}
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <TableContainer>
                      <Table>
                        <TableHead className={classes.tableDriveLicendt}>
                          <TableRow>
                            {rawText && (
                              <TableCell
                                sx={{
                                  color: "white",
                                  fontWeight: "bold",
                                  fontSize: "16px",
                                }}
                              >
                                Raw OCR Text
                              </TableCell>
                            )}
                            {parsed && (
                              <TableCell
                                sx={{
                                  color: "white",
                                  fontWeight: "bold",
                                  fontSize: "16px",
                                }}
                              >
                                Parsed Data
                              </TableCell>
                            )}
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          <TableRow hover>
                            {rawText && (
                              <TableCell
                                sx={{
                                  verticalAlign: "top",
                                  backgroundColor: "#fafafa",
                                }}
                              >
                                <pre
                                  style={{
                                    whiteSpace: "pre-wrap",
                                    // background: "#f5f5f5",
                                    padding: "12px",
                                    // borderRadius: "8px",
                                    fontSize: "14px",
                                    fontFamily: "monospace",
                                    maxHeight: "500px",
                                    overflowY: "auto",
                                  }}
                                >
                                  {rawText}
                                </pre>
                              </TableCell>
                            )}

                            {parsed && (
                              <TableCell sx={{ verticalAlign: "top" }}>
                                <Box
                                  component="ul"
                                  sx={{ listStyle: "none", p: 0, m: 0 }}
                                >
                                  <Box component="li" sx={{ mb: 1 }}>
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      License Number
                                    </Typography>
                                    <Typography
                                      variant="subtitle1"
                                      fontWeight="bold"
                                    >
                                      {parsed.licenseNumber || "-"}
                                    </Typography>
                                  </Box>

                                  <Box component="li" sx={{ mb: 1 }}>
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      First Name
                                    </Typography>
                                    <Typography
                                      variant="subtitle1"
                                      fontWeight="bold"
                                    >
                                      {parsed.firstName || "-"}
                                    </Typography>
                                  </Box>

                                  <Box component="li" sx={{ mb: 1 }}>
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      Last Name
                                    </Typography>
                                    <Typography
                                      variant="subtitle1"
                                      fontWeight="bold"
                                    >
                                      {parsed.lastName || "-"}
                                    </Typography>
                                  </Box>

                                  <Box component="li" sx={{ mb: 1 }}>
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      Address
                                    </Typography>
                                    <Typography
                                      variant="subtitle1"
                                      fontWeight="bold"
                                    >
                                      {parsed.address || "-"}
                                    </Typography>
                                  </Box>

                                  <Box component="li" sx={{ mb: 1 }}>
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      Sex
                                    </Typography>
                                    <Typography
                                      variant="subtitle1"
                                      fontWeight="bold"
                                    >
                                      {parsed.sex || "-"}
                                    </Typography>
                                  </Box>

                                  <Box component="li" sx={{ mb: 1 }}>
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      Date of Birth
                                    </Typography>
                                    <Typography
                                      variant="subtitle1"
                                      fontWeight="bold"
                                    >
                                      {parsed.dateOfBirth || "-"}
                                    </Typography>
                                  </Box>

                                  <Box component="li" sx={{ mb: 1 }}>
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      Issue Date
                                    </Typography>
                                    <Typography
                                      variant="subtitle1"
                                      fontWeight="bold"
                                    >
                                      {parsed.issueDate || "-"}
                                    </Typography>
                                  </Box>

                                  <Box component="li" sx={{ mb: 1 }}>
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      Expiry Date
                                    </Typography>
                                    <Typography
                                      variant="subtitle1"
                                      fontWeight="bold"
                                    >
                                      {parsed.expiryDate || "-"}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                            )}
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          )}
          {parsed && (
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <FormikProvider value={formik}>
                <form onSubmit={formik.handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Card className={classes.card}>
                        <Grid
                          container
                          justifyContent={"space-between"}
                          spacing={5}
                          sx={{ borderBottom: 2, padding: 2 }}
                          className={classes.titleGrid}
                        >
                          <Grid
                            item
                            lg={12}
                            md={12}
                            sm={12}
                            xs={12}
                            pt={2}
                            alignContent={"center"}
                          >
                            <Typography className={classes.cardTitle}>
                              Driver License Information
                            </Typography>
                          </Grid>
                        </Grid>

                        <Grid container spacing={3} p={"24px"}>
                          <Grid item lg={6} md={6} sm={12} xs={12}>
                            <Grid container alignItems="center">
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <Typography variant="subtitle1">
                                  License Number{" "}
                                  <span className={classes.requiedfield}>
                                    *
                                  </span>
                                </Typography>
                              </Grid>
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <TextField
                                  placeholder="License Number"
                                  size="small"
                                  {...formik.getFieldProps("license_number")}
                                  className={classes.textField}
                                  InputProps={{
                                    sx: {
                                      fontSize: 14,
                                    },
                                  }}
                                  InputLabelProps={{
                                    sx: {
                                      fontSize: 14,
                                    },
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Grid container spacing={2}>
                              <Grid item lg={6} md={6} sm={12} xs={12}>
                                <Grid container alignItems="center">
                                  <Grid item lg={12} md={12} sm={12} xs={12}>
                                    <Typography variant="subtitle1">
                                      First Name{" "}
                                      <span className={classes.requiedfield}>
                                        *
                                      </span>
                                    </Typography>
                                  </Grid>
                                  <Grid item lg={12} md={12} sm={12} xs={12}>
                                    <TextField
                                      placeholder="First Name"
                                      size="small"
                                      {...formik.getFieldProps("first_name")}
                                      className={classes.textField}
                                      InputProps={{
                                        sx: {
                                          fontSize: 14,
                                        },
                                      }}
                                      InputLabelProps={{
                                        sx: {
                                          fontSize: 14,
                                        },
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item lg={6} md={6} sm={12} xs={12}>
                                <Grid container alignItems="center">
                                  <Grid item lg={12} md={12} sm={12} xs={12}>
                                    <Typography variant="subtitle1">
                                      Last Name{" "}
                                      <span className={classes.requiedfield}>
                                        *
                                      </span>
                                    </Typography>
                                  </Grid>
                                  <Grid item lg={12} md={12} sm={12} xs={12}>
                                    <TextField
                                      placeholder="Last Name"
                                      size="small"
                                      {...formik.getFieldProps("last_name")}
                                      className={classes.textField}
                                      InputProps={{
                                        sx: {
                                          fontSize: 14,
                                        },
                                      }}
                                      InputLabelProps={{
                                        sx: {
                                          fontSize: 14,
                                        },
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>

                          <Grid item lg={6} md={6} sm={12} xs={12}>
                            <Grid container alignItems="center">
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <Typography variant="subtitle1">
                                  Sex{" "}
                                </Typography>
                              </Grid>
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <TextField
                                  select
                                  size="small"
                                  className={classes.textField}
                                  SelectProps={{
                                    native: false,
                                  }}
                                  defaultValue=""
                                  InputLabelProps={{ shrink: true }}
                                  {...formik.getFieldProps("sex")}
                                >
                                  <MenuItem value="" disabled>
                                    Select an option
                                  </MenuItem>

                                  {Object.entries(GenderalidationDriver).map(
                                    ([key, value], index) => (
                                      <MenuItem key={index} value={value}>
                                        {key}
                                      </MenuItem>
                                    )
                                  )}
                                </TextField>
                              </Grid>
                            </Grid>
                          </Grid>

                          <Grid item lg={6} md={6} sm={12} xs={12}>
                            <Grid container alignItems="center">
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <Typography variant="subtitle1">
                                  Date of Birth
                                </Typography>
                              </Grid>
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <LocalizationProvider
                                  dateAdapter={AdapterDayjs}
                                >
                                  <DatePicker
                                    value={formik.values.date_of_birth}
                                    onChange={(date_of_birth) =>
                                      formik.setFieldValue(
                                        "date_of_birth",
                                        date_of_birth
                                      )
                                    }
                                    slotProps={{
                                      textField: {
                                        placeholder: "Date of Birth",
                                        size: "small",
                                        sx: {
                                          width: "100%",
                                        },
                                        InputProps: {
                                          sx: {
                                            fontSize: 14,
                                          },
                                        },
                                        InputLabelProps: {
                                          sx: {
                                            fontSize: 14,
                                          },
                                        },
                                      },
                                    }}
                                  />
                                </LocalizationProvider>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Grid container alignItems="center">
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <Typography variant="subtitle1">
                                  Address
                                </Typography>
                              </Grid>
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <TextField
                                  placeholder="Address"
                                  size="small"
                                  multiline
                                  rows={2}
                                  {...formik.getFieldProps("address")}
                                  className={classes.textField}
                                  InputProps={{
                                    sx: {
                                      fontSize: 14,
                                    },
                                  }}
                                  InputLabelProps={{
                                    sx: {
                                      fontSize: 14,
                                    },
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item lg={6} md={6} sm={12} xs={12}>
                            <Grid container alignItems="center">
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <Typography variant="subtitle1">
                                  Issue Date
                                </Typography>
                              </Grid>
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <LocalizationProvider
                                  dateAdapter={AdapterDayjs}
                                >
                                  <DatePicker
                                    value={formik.values.issue_date}
                                    onChange={(issue_date) =>
                                      formik.setFieldValue(
                                        "issue_date",
                                        issue_date
                                      )
                                    }
                                    slotProps={{
                                      textField: {
                                        placeholder: "Issue Date",
                                        size: "small",
                                        sx: {
                                          width: "100%",
                                        },
                                        InputProps: {
                                          sx: {
                                            fontSize: 14,
                                          },
                                        },
                                        InputLabelProps: {
                                          sx: {
                                            fontSize: 14,
                                          },
                                        },
                                      },
                                    }}
                                  />
                                </LocalizationProvider>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item lg={6} md={6} sm={12} xs={12}>
                            <Grid container alignItems="center">
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <Typography variant="subtitle1">
                                  Expiry Date
                                </Typography>
                              </Grid>
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <LocalizationProvider
                                  dateAdapter={AdapterDayjs}
                                >
                                  <DatePicker
                                    value={formik.values.expiry_date}
                                    onChange={(expiry_date) =>
                                      formik.setFieldValue(
                                        "expiry_date",
                                        expiry_date
                                      )
                                    }
                                    slotProps={{
                                      textField: {
                                        placeholder: "Expiry Date",
                                        size: "small",
                                        sx: {
                                          width: "100%",
                                        },
                                        InputProps: {
                                          sx: {
                                            fontSize: 14,
                                          },
                                        },
                                        InputLabelProps: {
                                          sx: {
                                            fontSize: 14,
                                          },
                                        },
                                      },
                                    }}
                                  />
                                </LocalizationProvider>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Card>
                    </Grid>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Card className={classes.card}>
                        <Grid container spacing={3} p={"24px"}>
                          <Grid
                            item
                            lg={12}
                            md={12}
                            sm={12}
                            xs={12}
                            sx={{ display: "flex", justifyContent: "flex-end" }}
                          >
                            <Button
                              variant="contained"
                              startIcon={
                                isLoading ? (
                                  <CircularProgress
                                    size={18}
                                    className={classes.isLoading}
                                  />
                                ) : (
                                  <SaveAltIcon />
                                )
                              }
                              className={classes.addButton}
                              onClick={() => formik.handleSubmit()}
                              // disabled={!formValid}
                            >
                              Confirm & Save
                            </Button>
                          </Grid>
                        </Grid>
                      </Card>
                    </Grid>
                  </Grid>
                </form>
              </FormikProvider>
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
}
